 /**
   * @template {unknown} CallbackReturn
   * @template {unknown[]} CallbackParams
   *
   * @callback CustomCallback
   *
   * @param {CallbackParams} params
   * @returns {CallbackReturn}
   */

   /**
   * @template {unknown[]} DebounceParams
   *
   * @typedef DebounceCallbackWithMethods
   * @property {() => void} clear - Removing the render from the queue without execution
   * @property {(args?: DebounceParams) => void} flush - Execute render if it is in the queue
   * @property {(args?: DebounceParams) => void} callImmediate - Immediate execute render
   */


export class Debounce {
  #global;
  #isInProgress = false;

  /** @type {Function[]} */
  #resolves = [];

  /** @type {number?} */
  #debounceId = null;

    /** @type {unknown} */
  #context;
    /** @type {any} */
  #callback;

  /**
  * @constructor
  * @param {Object} param
  * @param {Window} param.global
  */
  constructor({ global }) {
    this.#global = global;
  }

  /**
   * @template {unknown} Return
   * @template {unknown[]} Params
   * @template {unknown} Context
   * @param {CustomCallback<Return,Params>} callback
   * @param {Window|Context} [context=this.#global]
   * @returns {DebounceCallbackWithMethods<Params> & (() => Promise<void>)}
   */
  create(callback, context = this.#global) {
    this.#context = context;
    this.#callback = callback;

    const debounceCallback = (/** @type {any} */...args) => {
      return /** @type {Promise<void>} */(new Promise((resolve) => {
        this.#clear();
        this.#isInProgress = true;
        this.#resolves.push(() => resolve());
        this.#debounceId = this.#global.requestAnimationFrame(() => this.#flush(...args));
      }));
    };

    debounceCallback.clear = () => this.#clear();
    debounceCallback.flush = (/** @type {any} */...args) => this.#flush(...args);
    debounceCallback.callImmediate = (/** @type {any} */...args) => this.#callImmediate(...args);
    return debounceCallback;
  }


  /**
   * @param {unknown[]} args
   */
  #callImmediate(...args) {
    this.#callback.bind(this.#context)(...args);
  }

  /**
   * @param {unknown[]} args
   */
  #flush(...args) {
    if (!this.#isInProgress) {
      return;
    }

    this.#callImmediate(...args);
    this.#resolves.forEach((resolve) => {
      resolve();
    });
    this.#resolves = [];
    this.#clear();
  }

  #clear() {
    if (!this.#isInProgress) {
      return;
    }

    if(this.#debounceId) {
      this.#global.cancelAnimationFrame(this.#debounceId);
      this.#debounceId = null;
    }
    this.#isInProgress = false;
  }
}
